'use client'

import { useRef } from 'react'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import type { C2CRouteCopy } from '@/lib/app-shell/copy'
import { cn } from '@/lib/utils'

/** A date the strip offers, with the day and month names it shows for it. */
export interface DateStripDay {
    /** `YYYY-MM-DD`. */
    readonly date: string
    readonly dayName: string
    readonly monthDay: string
}

interface DateSelectorProps {
    /** The dates offered, named by the server in the render locale. */
    dates: readonly DateStripDay[]
    selectedDate: string
    onDateChange: (date: string) => void
    /** The scroll controls' accessible names. */
    previousDates: DateSelectorCopy['previousDates']
    nextDates: DateSelectorCopy['nextDates']
}

export type DateSelectorCopy = Pick<C2CRouteCopy, 'nextDates' | 'previousDates'>

/**
 * Renders the names it is handed and formats none: a name formatted here would hydrate
 * against the browser's locale data rather than the server's. The scroll controls'
 * accessible names are the shell's copy, rendered by the page.
 */
export function DateSelector({
    dates,
    selectedDate,
    onDateChange,
    previousDates,
    nextDates,
}: DateSelectorProps) {
    const containerRef = useRef<HTMLOListElement>(null)

    function scrollLeft() {
        const el = containerRef.current
        if (!el?.scrollBy) {
            return
        }
        el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' })
    }

    function scrollRight() {
        const el = containerRef.current
        if (!el?.scrollBy) {
            return
        }
        el.scrollBy({ left: el.clientWidth, behavior: 'smooth' })
    }

    return (
        <div className="flex items-center py-4 md:py-12">
            <button
                type="button"
                aria-label={previousDates.text}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#2e3033] hover:bg-[#eaebec] disabled:text-[#b6bdc7]"
                onClick={scrollLeft}
            >
                <ChevronLeft className="h-5 w-5" />
            </button>

            <ol
                ref={containerRef}
                className="scrollbar-none flex min-w-0 flex-1 gap-1 overflow-x-auto"
            >
                {dates.map(({ date, dayName, monthDay }) => {
                    const isSelected = date === selectedDate

                    return (
                        <li key={date}>
                            <button
                                type="button"
                                className={cn(
                                    'flex h-[64px] w-[80px] flex-col items-center justify-center rounded-[7px] text-sm transition-colors md:h-[72px] md:w-[88px] md:text-base',
                                    isSelected
                                        ? 'bg-[#188da1] text-white'
                                        : 'text-[#2e3033] hover:bg-[#eaebec]',
                                )}
                                onClick={() => onDateChange(date)}
                            >
                                <span className="font-bold">{dayName}</span>
                                <span className="font-bold">{monthDay}</span>
                            </button>
                        </li>
                    )
                })}
            </ol>

            <button
                type="button"
                aria-label={nextDates.text}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#2e3033] hover:bg-[#eaebec] disabled:text-[#b6bdc7]"
                onClick={scrollRight}
            >
                <ChevronRight className="h-5 w-5" />
            </button>
        </div>
    )
}
